import { Editor } from '@tiptap/react'
import { EditorSelection, PaperStatus } from '@chegg-wt/core-wa/types'
import { SentenceOffset } from '@chegg-wt/core-wa/state/types'
import {
  setPaperId,
  setPaperList,
  setPaperTitle,
  setSentenceOffsets,
  updatePaperHandler,
} from '@chegg-wt/core-wa/state/actions'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import {
  createGuestUserPaper,
  mapSentencesToSentenceOffsets,
  generateUniquePaperTitle,
} from '@chegg-wt/core-wa/utils/helpers'
import { handleManualEdits } from '@chegg-wt/core-wa/utils/manualEditHelpers'
import history from '@chegg-wt/core-wa/utils/history/history'
import {
  FetchSentenceOffsets,
  FetchSentenceOffsetsProps,
  GetUpdateSentenceOffsetsReturnType,
} from '../types'

const nonBoundaryInBetween = (
  from: number,
  to: number,
  fromIndex: number,
  toIndex: number,
  sentenceOffsets: SentenceOffset[]
) => {
  const { offset: fromSentenceOffset, length: fromSentenceLength } = sentenceOffsets[
    fromIndex
  ]
  const fromSentenceEnd = fromSentenceOffset + fromSentenceLength
  const fromLength = fromSentenceEnd - from + 1

  const { offset: toSentenceOffset } = sentenceOffsets[toIndex]
  const toLength = to - toSentenceOffset + 1

  let lengthSentenceInBetween = 0

  for (let sentenceIndex = fromIndex + 1; sentenceIndex < toIndex; sentenceIndex += 1) {
    lengthSentenceInBetween += sentenceOffsets[sentenceIndex].length + 1
  }

  const totalSentencesLength = fromLength + toLength + lengthSentenceInBetween
  const noBoundaryInBetween = totalSentencesLength === to - from + 1

  return !noBoundaryInBetween
}

export const nonBoundaryText = (
  selection: EditorSelection,
  sentenceOffsets: SentenceOffset[]
) => {
  const { from, to } = selection

  let fromFound = -1
  let toFound = -1

  for (
    let sentenceIndex = 0;
    sentenceIndex < sentenceOffsets.length;
    sentenceIndex += 1
  ) {
    const { length, offset } = sentenceOffsets[sentenceIndex]
    const totalLength = offset + length

    if (fromFound === -1 && from < offset - 1) break

    if (fromFound === -1 && from >= offset - 1 && from <= totalLength - 1) {
      fromFound = sentenceIndex

      if (to <= totalLength) {
        toFound = sentenceIndex
        break
      }
    } else if (fromFound !== -1 && to <= offset + length) {
      toFound = sentenceIndex
      if (to < offset) return true
      if (nonBoundaryInBetween(from, to, fromFound, toFound, sentenceOffsets)) return true
      return false
    }
  }

  return toFound === -1
}

export const getUpdatedSentenceOffsets = (
  currentSentencesOffsets: SentenceOffset[],
  newSentencesOffsets: SentenceOffset[]
): GetUpdateSentenceOffsetsReturnType => {
  const updatedSentenceOffsets: SentenceOffset[] = []
  const addedSentences: Omit<SentenceOffset, 'originalText' | 'lastUpdated'>[] = []
  const removedSentences: Omit<SentenceOffset, 'originalText' | 'lastUpdated'>[] = []
  let currentSentenceIndex = 0
  let newSentenceIndex = 0

  for (
    newSentenceIndex = 0;
    newSentenceIndex < newSentencesOffsets.length;
    newSentenceIndex += 1
  ) {
    if (currentSentenceIndex === currentSentencesOffsets.length) break

    const {
      offset: newSentenceOffset,
      length: newSentenceLength,
      id: newSentenceId,
    } = newSentencesOffsets[newSentenceIndex]

    let {
      offset: currentSentenceOffset,
      length: currentSentenceLength,
      id: currentSentenceId,
    } = currentSentencesOffsets[currentSentenceIndex]

    // as we are storing offsets++ inside setSentenceOffsets action for our state
    while (currentSentenceOffset + currentSentenceLength < newSentenceOffset + 1) {
      currentSentenceIndex += 1
      removedSentences.push({
        id: currentSentenceId,
        offset: currentSentenceOffset,
        length: currentSentenceLength,
      })
      if (currentSentenceIndex === currentSentencesOffsets.length) break
      ;({
        offset: currentSentenceOffset,
        length: currentSentenceLength,
        id: currentSentenceId,
      } = currentSentencesOffsets[currentSentenceIndex])
    }

    if (
      currentSentenceOffset === newSentenceOffset + 1 &&
      currentSentenceLength === newSentenceLength
    ) {
      updatedSentenceOffsets.push({
        ...currentSentencesOffsets[currentSentenceIndex],
        offset: currentSentencesOffsets[currentSentenceIndex].offset - 1,
      })

      currentSentenceIndex += 1
    } else {
      addedSentences.push({
        id: newSentenceId,
        offset: newSentenceOffset + 1, // +1 as offsets are store as 1 based index in redux
        length: newSentenceLength,
      })
      updatedSentenceOffsets.push(newSentencesOffsets[newSentenceIndex])
    }
  }

  for (let i = currentSentenceIndex; i < currentSentencesOffsets.length; i += 1) {
    const {
      offset: currentSentenceOffset,
      length: currentSentenceLength,
      id: currentSentenceId,
    } = currentSentencesOffsets[i]
    removedSentences.push({
      id: currentSentenceId,
      offset: currentSentenceOffset,
      length: currentSentenceLength,
    })
  }

  for (let i = newSentenceIndex; i < newSentencesOffsets.length; i += 1) {
    const {
      offset: newSentenceOffset,
      length: newSentenceLength,
      id: newSentenceId,
    } = newSentencesOffsets[i]
    addedSentences.push({
      id: newSentenceId,
      offset: newSentenceOffset + 1, // +1 as offsets are store as 1 based index in redux
      length: newSentenceLength,
    })
    updatedSentenceOffsets.push(newSentencesOffsets[i])
  }

  history.addSentenceOffsetDelta({
    removedSentences,
    addedSentences,
  })

  return { updatedSentenceOffsets, removedSentences }
}

export const fetchSentenceOffsets = async ({
  sentenceOffsets,
  selection,
  paperId,
  paperTitle,
  feedbacks,
  paperState,
  dispatch,
  isLoggedIn,
  paperList,
}: FetchSentenceOffsetsProps): Promise<FetchSentenceOffsets> => {
  if (sentenceOffsets.length && !nonBoundaryText(selection, sentenceOffsets)) {
    return { sentences: sentenceOffsets, paperId }
  }

  const editor: Editor = getFeatureEditor()
  const editorText = editor.getText()
  const paperContent = {
    editorJSON: editor.getJSON(),
  }

  let newTitle: string = paperTitle
  // We will only add title when creating paper first time
  if (!paperTitle && !paperId) {
    newTitle = generateUniquePaperTitle(paperList)
    dispatch(setPaperTitle(newTitle))
  }

  // create paper for guest user
  const currentPaperId = isLoggedIn
    ? paperId
    : await createGuestUserPaper({
        paperId,
        title: newTitle,
        paperContent,
        status: PaperStatus.DRAFT,
      })

  const { sentences, paperId: newPaperId } = await oneGraph.splitTextIntoSentences({
    paperText: editorText,
    paperId: paperId ?? currentPaperId,
  })

  if (!paperId) {
    dispatch(setPaperId(newPaperId))
  }
  const mappedSentences: SentenceOffset[] = mapSentencesToSentenceOffsets(
    sentences,
    editorText
  )

  const { updatedSentenceOffsets, removedSentences } = getUpdatedSentenceOffsets(
    sentenceOffsets,
    mappedSentences
  )

  if (isLoggedIn) {
    const updatedPaperList = [...paperList]
    let paperIndex = updatedPaperList.findIndex((paper) => paper.paperId === newPaperId)
    if (paperIndex === -1) {
      paperIndex = 0
    }
    const updatedPaper = {
      ...updatedPaperList[paperIndex],
      title: newTitle,
      paperId: newPaperId,
    }
    updatedPaperList[paperIndex] = updatedPaper
    dispatch(setPaperList([...updatedPaperList]))
    dispatch(updatePaperHandler())
  }

  if (paperState !== PaperStatus.DRAFT) {
    // Remove the cards for removed sentences
    handleManualEdits(
      removedSentences,
      sentenceOffsets, // Passing old setneces not updated one
      feedbacks,
      dispatch,
      editor,
      null
    )
    editor.commands.setTextSelection(selection)
  }

  // received paperId for free/paid user
  dispatch(setSentenceOffsets(updatedSentenceOffsets))
  return { sentences: updatedSentenceOffsets, paperId: newPaperId }
}
